import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  activatePaymentGateway,
  getPaymentFinance,
  getPaymentGateways,
  getPaymentReviews,
  getPaymentStats,
  resolveMediaUrl,
  reviewManualPayment,
  updatePaymentFinance,
  updatePaymentGateway,
  uploadManualQrImage,
} from '../../api';
import {
  PageHeader, StatusBadge, Table, Pagination, EmptyState, TableSkeleton, UserCell,
} from '../../components/ui';
import OverviewBar from '../../components/ui/OverviewBar';
import Modal from '../../components/ui/Modal';
import { formatCurrency, formatDateTime } from '../../utils/format';
import { CreditCard, Save, CheckCircle2, Eye } from 'lucide-react';

type Tab = 'gateways' | 'reviews' | 'finance' | 'stats';

export default function PaymentGatewaysPage() {
  const [tab, setTab] = useState<Tab>('gateways');
  const qc = useQueryClient();

  const { data: gwData, isLoading: gwLoading } = useQuery({
    queryKey: ['payment-gateways'],
    queryFn: () => getPaymentGateways().then((r) => r.data.data),
  });

  const { data: financeData, isLoading: finLoading } = useQuery({
    queryKey: ['payment-finance'],
    queryFn: () => getPaymentFinance().then((r) => r.data.data.finance),
  });

  const [financeForm, setFinanceForm] = useState<any>(null);
  useEffect(() => {
    if (financeData) setFinanceForm({ ...financeData });
  }, [financeData]);

  const [razorpayForm, setRazorpayForm] = useState<any>({});
  const [manualForm, setManualForm] = useState<any>({});
  useEffect(() => {
    const list = gwData?.gateways || [];
    const rp = list.find((g: any) => g.code === 'razorpay');
    const mn = list.find((g: any) => g.code === 'manual');
    if (rp) {
      setRazorpayForm({
        name: rp.name,
        description: rp.description || '',
        isEnabled: rp.isEnabled,
        keyId: rp.razorpay?.keyId || '',
        keySecret: '',
        webhookSecret: '',
      });
    }
    if (mn) {
      setManualForm({
        name: mn.name,
        description: mn.description || '',
        isEnabled: mn.isEnabled,
        ...(mn.manual || {}),
      });
    }
  }, [gwData]);

  const saveRp = useMutation({
    mutationFn: () =>
      updatePaymentGateway('razorpay', {
        name: razorpayForm.name,
        description: razorpayForm.description,
        isEnabled: razorpayForm.isEnabled,
        razorpay: {
          keyId: razorpayForm.keyId,
          keySecret: razorpayForm.keySecret || undefined,
          webhookSecret: razorpayForm.webhookSecret || undefined,
        },
      }),
    onSuccess: () => {
      toast.success('Razorpay settings saved');
      qc.invalidateQueries({ queryKey: ['payment-gateways'] });
      setRazorpayForm((p: any) => ({ ...p, keySecret: '', webhookSecret: '' }));
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const saveManual = useMutation({
    mutationFn: () =>
      updatePaymentGateway('manual', {
        name: manualForm.name,
        description: manualForm.description,
        isEnabled: manualForm.isEnabled,
        manual: {
          upiId: manualForm.upiId,
          accountName: manualForm.accountName,
          accountNumber: manualForm.accountNumber,
          ifsc: manualForm.ifsc,
          bankName: manualForm.bankName,
          qrImageUrl: manualForm.qrImageUrl,
          instructions: manualForm.instructions,
        },
      }),
    onSuccess: () => {
      toast.success('Manual gateway settings saved');
      qc.invalidateQueries({ queryKey: ['payment-gateways'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const activateMut = useMutation({
    mutationFn: (code: string) => activatePaymentGateway(code),
    onSuccess: (r) => {
      toast.success(r.data.message || 'Gateway activated');
      qc.invalidateQueries({ queryKey: ['payment-gateways'] });
      qc.invalidateQueries({ queryKey: ['payment-stats'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Activation failed'),
  });

  const saveFinance = useMutation({
    mutationFn: () => updatePaymentFinance(financeForm),
    onSuccess: () => {
      toast.success('Finance settings saved');
      qc.invalidateQueries({ queryKey: ['payment-finance'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const onQrUpload = async (file: File) => {
    try {
      const res = await uploadManualQrImage(file);
      const url = res.data?.data?.url;
      if (url) {
        setManualForm((p: any) => ({ ...p, qrImageUrl: url }));
        toast.success('QR uploaded');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Upload failed');
    }
  };

  const gateways = gwData?.gateways || [];
  const activeCode = gwData?.activeCode;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'gateways', label: 'Gateways' },
    { id: 'reviews', label: 'Manual reviews' },
    { id: 'finance', label: 'Tax & charges' },
    { id: 'stats', label: 'Gateway stats' },
  ];

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Payment gateways"
        subtitle="Activate one gateway, configure Razorpay or manual bank/UPI, tax and charges"
      />

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm rounded-lg font-medium ${
              tab === t.id ? 'bg-primary-50 text-primary-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'gateways' && (
        <div className="space-y-4">
          {gwLoading ? <TableSkeleton rows={2} cols={3} /> : (
            <>
              <OverviewBar
                items={[
                  { label: 'Active gateway', value: activeCode || 'None' },
                  { label: 'Configured', value: String(gateways.length) },
                ]}
              />
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {/* Razorpay card */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard size={18} className="text-primary-600" />
                      <h3 className="font-semibold text-gray-900">Razorpay</h3>
                      {activeCode === 'razorpay' && <StatusBadge status="active" />}
                    </div>
                    <button
                      type="button"
                      disabled={activateMut.isPending || activeCode === 'razorpay'}
                      onClick={() => activateMut.mutate('razorpay')}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 disabled:opacity-50"
                    >
                      {activeCode === 'razorpay' ? 'Active' : 'Activate'}
                    </button>
                  </div>
                  <label className="block text-xs text-gray-500">Display name
                    <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={razorpayForm.name || ''} onChange={(e) => setRazorpayForm((p: any) => ({ ...p, name: e.target.value }))} />
                  </label>
                  <label className="block text-xs text-gray-500">Key ID
                    <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono" value={razorpayForm.keyId || ''} onChange={(e) => setRazorpayForm((p: any) => ({ ...p, keyId: e.target.value }))} />
                  </label>
                  <label className="block text-xs text-gray-500">Key Secret (leave blank to keep)
                    <input type="password" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono" value={razorpayForm.keySecret || ''} onChange={(e) => setRazorpayForm((p: any) => ({ ...p, keySecret: e.target.value }))} placeholder="••••••••" />
                  </label>
                  <label className="block text-xs text-gray-500">Webhook secret (leave blank to keep)
                    <input type="password" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm font-mono" value={razorpayForm.webhookSecret || ''} onChange={(e) => setRazorpayForm((p: any) => ({ ...p, webhookSecret: e.target.value }))} placeholder="••••••••" />
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={!!razorpayForm.isEnabled} onChange={(e) => setRazorpayForm((p: any) => ({ ...p, isEnabled: e.target.checked }))} />
                    Enabled in catalog
                  </label>
                  <button type="button" onClick={() => saveRp.mutate()} disabled={saveRp.isPending} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium">
                    <Save size={14} /> Save Razorpay
                  </button>
                </div>

                {/* Manual card */}
                <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={18} className="text-amber-600" />
                      <h3 className="font-semibold text-gray-900">Manual / Bank transfer</h3>
                      {activeCode === 'manual' && <StatusBadge status="active" />}
                    </div>
                    <button
                      type="button"
                      disabled={activateMut.isPending || activeCode === 'manual'}
                      onClick={() => activateMut.mutate('manual')}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 disabled:opacity-50"
                    >
                      {activeCode === 'manual' ? 'Active' : 'Activate'}
                    </button>
                  </div>
                  <label className="block text-xs text-gray-500">UPI ID
                    <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={manualForm.upiId || ''} onChange={(e) => setManualForm((p: any) => ({ ...p, upiId: e.target.value }))} />
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block text-xs text-gray-500">Account name
                      <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={manualForm.accountName || ''} onChange={(e) => setManualForm((p: any) => ({ ...p, accountName: e.target.value }))} />
                    </label>
                    <label className="block text-xs text-gray-500">Bank name
                      <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={manualForm.bankName || ''} onChange={(e) => setManualForm((p: any) => ({ ...p, bankName: e.target.value }))} />
                    </label>
                    <label className="block text-xs text-gray-500">Account number
                      <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={manualForm.accountNumber || ''} onChange={(e) => setManualForm((p: any) => ({ ...p, accountNumber: e.target.value }))} />
                    </label>
                    <label className="block text-xs text-gray-500">IFSC
                      <input className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={manualForm.ifsc || ''} onChange={(e) => setManualForm((p: any) => ({ ...p, ifsc: e.target.value }))} />
                    </label>
                  </div>
                  <label className="block text-xs text-gray-500">Instructions for users
                    <textarea className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" rows={3} value={manualForm.instructions || ''} onChange={(e) => setManualForm((p: any) => ({ ...p, instructions: e.target.value }))} />
                  </label>
                  <div className="flex items-center gap-3">
                    {manualForm.qrImageUrl ? (
                      <img src={resolveMediaUrl(manualForm.qrImageUrl)} alt="QR" className="h-16 w-16 object-contain border rounded" />
                    ) : null}
                    <label className="text-xs font-medium text-primary-700 cursor-pointer">
                      Upload QR image
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onQrUpload(e.target.files[0])} />
                    </label>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={!!manualForm.isEnabled} onChange={(e) => setManualForm((p: any) => ({ ...p, isEnabled: e.target.checked }))} />
                    Enabled in catalog
                  </label>
                  <button type="button" onClick={() => saveManual.mutate()} disabled={saveManual.isPending} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium">
                    <Save size={14} /> Save Manual
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-500">Only one gateway can be active. Users pay the remainder via that gateway after using wallet balance (except wallet recharge, which is full gateway).</p>
            </>
          )}
        </div>
      )}

      {tab === 'reviews' && <ManualReviewsTab />}

      {tab === 'finance' && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 max-w-xl space-y-3">
          {finLoading || !financeForm ? <TableSkeleton rows={4} cols={2} /> : (
            <>
              <label className="block text-xs text-gray-500">Tax / GST %
                <input type="number" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={financeForm.taxPercent ?? 0} onChange={(e) => setFinanceForm((p: any) => ({ ...p, taxPercent: Number(e.target.value) }))} />
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!financeForm.taxInclusive} onChange={(e) => setFinanceForm((p: any) => ({ ...p, taxInclusive: e.target.checked }))} />
                Prices are tax-inclusive
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!financeForm.gatewayFeeEnabled} onChange={(e) => setFinanceForm((p: any) => ({ ...p, gatewayFeeEnabled: e.target.checked }))} />
                Add gateway convenience fee
              </label>
              <label className="block text-xs text-gray-500">Gateway fee %
                <input type="number" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={financeForm.gatewayFeePercent ?? 0} onChange={(e) => setFinanceForm((p: any) => ({ ...p, gatewayFeePercent: Number(e.target.value) }))} />
              </label>
              <label className="block text-xs text-gray-500">Consultation platform fee %
                <input type="number" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={financeForm.consultationPlatformFeePercent ?? 20} onChange={(e) => setFinanceForm((p: any) => ({ ...p, consultationPlatformFeePercent: Number(e.target.value) }))} />
              </label>
              <label className="block text-xs text-gray-500">Min recharge (paise)
                <input type="number" className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" value={financeForm.minRechargePaise ?? 10000} onChange={(e) => setFinanceForm((p: any) => ({ ...p, minRechargePaise: Number(e.target.value) }))} />
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!financeForm.allowWalletOnly} onChange={(e) => setFinanceForm((p: any) => ({ ...p, allowWalletOnly: e.target.checked }))} />
                Allow full wallet checkout
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!financeForm.allowHybrid} onChange={(e) => setFinanceForm((p: any) => ({ ...p, allowHybrid: e.target.checked }))} />
                Allow hybrid wallet + gateway
              </label>
              <button type="button" onClick={() => saveFinance.mutate()} disabled={saveFinance.isPending} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium">
                <Save size={14} /> Save finance settings
              </button>
            </>
          )}
        </div>
      )}

      {tab === 'stats' && <GatewayStatsTab />}
    </div>
  );
}

function ManualReviewsTab() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('pending');
  const [detail, setDetail] = useState<any>(null);
  const [note, setNote] = useState('');
  const qc = useQueryClient();
  const LIMIT = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['payment-reviews', page, status],
    queryFn: () => getPaymentReviews({ page, limit: LIMIT, status: status || undefined }).then((r) => r.data),
  });

  const reviewMut = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'approved' | 'rejected' }) =>
      reviewManualPayment(id, { decision, note: note || undefined }),
    onSuccess: () => {
      toast.success('Review saved');
      setDetail(null);
      setNote('');
      qc.invalidateQueries({ queryKey: ['payment-reviews'] });
      qc.invalidateQueries({ queryKey: ['payment-stats'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Review failed'),
  });

  const rows = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', ''].map((s) => (
          <button
            key={s || 'all'}
            type="button"
            onClick={() => { setStatus(s); setPage(1); }}
            className={`px-3 py-1 text-xs rounded-full border ${status === s ? 'bg-primary-50 border-primary-200 text-primary-700' : 'border-gray-200 text-gray-600'}`}
          >
            {s || 'all'}
          </button>
        ))}
      </div>
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={6} cols={5} /></div>
        ) : rows.length === 0 ? (
          <EmptyState message="No manual payment proofs" icon={<CreditCard size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="table-th">User</th>
                <th className="table-th">Amount</th>
                <th className="table-th">UTR</th>
                <th className="table-th">Status</th>
                <th className="table-th">Submitted</th>
                <th className="table-th" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r._id} className="border-b last:border-0">
                  <td className="table-td"><UserCell entity={r.user || { _id: r.userId }} /></td>
                  <td className="table-td">{formatCurrency(r.amount)}</td>
                  <td className="table-td font-mono text-xs">{r.manualProof?.utr || '—'}</td>
                  <td className="table-td"><StatusBadge status={r.review?.status || r.status} /></td>
                  <td className="table-td text-xs">{formatDateTime(r.manualProof?.submittedAt || r.updatedAt)}</td>
                  <td className="table-td">
                    <button type="button" className="text-primary-600 text-xs inline-flex items-center gap-1" onClick={() => setDetail(r)}>
                      <Eye size={14} /> Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
      {pagination && (
        <Pagination
          page={pagination.page}
          pages={pagination.pages}
          total={pagination.total}
          limit={LIMIT}
          onPageChange={setPage}
        />
      )}

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Manual payment review">
        {detail && (
          <div className="space-y-3 text-sm">
            <p><span className="text-gray-500">Amount:</span> {formatCurrency(detail.amount)} (wallet {formatCurrency(detail.walletAmount || 0)} + gateway {formatCurrency(detail.gatewayAmount || 0)})</p>
            <p><span className="text-gray-500">Payer:</span> {detail.manualProof?.payerName} · {detail.manualProof?.payerUpi || '—'}</p>
            <p><span className="text-gray-500">UTR:</span> {detail.manualProof?.utr}</p>
            <p><span className="text-gray-500">Notes:</span> {detail.manualProof?.notes || '—'}</p>
            <div className="flex flex-wrap gap-2">
              {(detail.manualProof?.screenshotUrls || []).map((url: string) => (
                <a key={url} href={resolveMediaUrl(url)} target="_blank" rel="noreferrer">
                  <img src={resolveMediaUrl(url)} alt="proof" className="h-24 w-24 object-cover rounded border" />
                </a>
              ))}
            </div>
            <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} placeholder="Admin note" value={note} onChange={(e) => setNote(e.target.value)} />
            {(detail.review?.status === 'pending' || detail.status === 'awaiting_review') && (
              <div className="flex gap-2">
                <button type="button" className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm" disabled={reviewMut.isPending} onClick={() => reviewMut.mutate({ id: detail._id, decision: 'approved' })}>Approve</button>
                <button type="button" className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm" disabled={reviewMut.isPending} onClick={() => reviewMut.mutate({ id: detail._id, decision: 'rejected' })}>Reject</button>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function GatewayStatsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['payment-stats'],
    queryFn: () => getPaymentStats({ days: 30 }).then((r) => r.data.data),
  });

  if (isLoading || !data) return <TableSkeleton rows={4} cols={4} />;

  const totals = data.totals || {};
  return (
    <div className="space-y-4">
      <OverviewBar
        items={[
          { label: 'Paid orders', value: String(totals.count || 0) },
          { label: 'Gross', value: formatCurrency(totals.grossPaise || 0) },
          { label: 'Tax', value: formatCurrency(totals.taxPaise || 0) },
          { label: 'Gateway fees', value: formatCurrency(totals.feePaise || 0) },
          { label: 'Pending manual', value: String(data.pendingManualReviews || 0) },
        ]}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-4">
          <h4 className="text-sm font-semibold mb-2">By provider</h4>
          {(data.byProvider || []).length === 0 ? <p className="text-xs text-gray-500">No data</p> : (
            <ul className="space-y-1 text-sm">
              {data.byProvider.map((r: any) => (
                <li key={r._id} className="flex justify-between">
                  <span>{r._id || 'unknown'}</span>
                  <span>{r.count} · {formatCurrency(r.grossPaise)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white border rounded-xl p-4">
          <h4 className="text-sm font-semibold mb-2">By purpose</h4>
          {(data.byPurpose || []).length === 0 ? <p className="text-xs text-gray-500">No data</p> : (
            <ul className="space-y-1 text-sm">
              {data.byPurpose.map((r: any) => (
                <li key={r._id} className="flex justify-between">
                  <span>{r._id || 'unknown'}</span>
                  <span>{r.count} · {formatCurrency(r.grossPaise)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
